import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { BUTTON_INTERACTIVE_CLASSES } from './Button'

/** Broad media kind used to pick the right inline preview widget. */
export type MediaGalleryKind = 'image' | 'video' | 'audio' | 'pdf' | 'other'

/** One file rendered by {@link MediaGallery}. */
export interface MediaGalleryItem {
  id: string
  fileName: string
  kind: MediaGalleryKind
  /** Optional secondary line rendered under the file name (e.g. type · upload date). */
  subtitle?: ReactNode
  /**
   * Resolves the URL to embed for inline preview (`<img>`/`<video>`/`<audio>` `src`, or an
   * `<iframe>` `src` for PDFs). Called once, lazily, the first time this item is rendered, and
   * cached for the component's lifetime — callers typically fetch a short-lived presigned URL
   * here, so this avoids requesting one for every item up front.
   */
  getPreviewUrl: () => Promise<string>
}

const DEFAULT_ITEM_CLASSES =
  'border-pax-purple/15 flex flex-col gap-3 rounded-xl border bg-slate-50 p-3'
// Info and actions are split into their own row groups so callers whose action list can grow
// (e.g. an organizer's Download + Delete) can override these to stack the actions under the file
// info on narrow viewports instead of squeezing everything into one row (see
// #media-mobile-layout). The default keeps everything on one line, which fits a single Download
// button comfortably even on narrow viewports.
const DEFAULT_HEADER_CLASSES = 'flex min-w-0 items-center gap-2'
const DEFAULT_INFO_CLASSES = 'flex min-w-0 flex-1 items-center gap-2'
const DEFAULT_ACTIONS_CLASSES = 'flex shrink-0 items-center gap-2'
const DEFAULT_ICON_CLASSES = 'text-pax-purple shrink-0'
const DEFAULT_FILENAME_CLASSES = 'text-pax-black min-w-0 flex-1 truncate text-sm font-medium'
const DEFAULT_DOWNLOAD_BUTTON_CLASSES =
  'text-pax-blue shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold hover:opacity-80'
const DEFAULT_DOWNLOAD_ALL_BUTTON_CLASSES =
  'border-pax-purple/30 text-pax-purple self-end rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50'

function ImageIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  )
}

function VideoIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  )
}

function AudioIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  )
}

function PdfIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  )
}

function FileIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M13 2v7h7" />
    </svg>
  )
}

const KIND_ICONS: Record<MediaGalleryKind, () => ReactNode> = {
  image: ImageIcon,
  video: VideoIcon,
  audio: AudioIcon,
  pdf: PdfIcon,
  other: FileIcon,
}

interface MediaGalleryCardProps {
  item: MediaGalleryItem
  onDownload: (item: MediaGalleryItem) => void
  downloadLabel: string
  previewUnavailableLabel: string
  itemClassName: string
  itemStyle?: CSSProperties
  headerClassName: string
  infoClassName: string
  actionsClassName: string
  iconClassName: string
  iconStyle?: CSSProperties
  fileNameClassName: string
  downloadButtonClassName: string
  downloadButtonStyle?: CSSProperties
  renderExtraActions?: (item: MediaGalleryItem) => ReactNode
}

function MediaGalleryCard({
  item,
  onDownload,
  downloadLabel,
  previewUnavailableLabel,
  itemClassName,
  itemStyle,
  headerClassName,
  infoClassName,
  actionsClassName,
  iconClassName,
  iconStyle,
  fileNameClassName,
  downloadButtonClassName,
  downloadButtonStyle,
  renderExtraActions,
}: MediaGalleryCardProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    item
      .getPreviewUrl()
      .then((url) => {
        if (!cancelled) setPreviewUrl(url)
      })
      .catch(() => {
        if (!cancelled) setPreviewFailed(true)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-resolve only when the item itself changes
  }, [item.id])

  const Icon = KIND_ICONS[item.kind]

  return (
    <div className={itemClassName} style={itemStyle}>
      <div className={headerClassName}>
        <div className={infoClassName}>
          <span className={iconClassName} style={iconStyle}>
            <Icon />
          </span>
          <span className="min-w-0 flex-1">
            <span className={`block ${fileNameClassName}`}>{item.fileName}</span>
            {item.subtitle && <span className="text-pax-gray block text-xs">{item.subtitle}</span>}
          </span>
        </div>
        <div className={actionsClassName}>
          <button
            type="button"
            onClick={() => onDownload(item)}
            className={`${BUTTON_INTERACTIVE_CLASSES} ${downloadButtonClassName}`}
            style={downloadButtonStyle}
          >
            {downloadLabel}
          </button>
          {renderExtraActions?.(item)}
        </div>
      </div>

      {previewUrl && item.kind === 'image' && (
        <img
          src={previewUrl}
          alt={item.fileName}
          className="max-h-64 w-full rounded-lg border border-black/5 object-contain"
        />
      )}
      {previewUrl && item.kind === 'video' && (
        <video src={previewUrl} controls className="w-full rounded-lg" />
      )}
      {previewUrl && item.kind === 'audio' && (
        <audio src={previewUrl} controls className="w-full" />
      )}
      {previewUrl && item.kind === 'pdf' && (
        <iframe
          src={previewUrl}
          title={item.fileName}
          className="h-72 w-full rounded-lg border border-black/5"
        />
      )}
      {previewFailed && <p className="text-pax-gray text-xs">{previewUnavailableLabel}</p>}
    </div>
  )
}

/** Props for {@link MediaGallery}. */
export interface MediaGalleryProps {
  items: MediaGalleryItem[]
  /** Called with the clicked item when its Download button is pressed. */
  onDownload: (item: MediaGalleryItem) => void
  /** Label for each item's Download button. Pass a translated string from the app. */
  downloadLabel: string
  /** Shown in place of the list when `items` is empty. */
  emptyLabel?: ReactNode
  /** Shown under an item whose preview failed to load. Falls back to English for package tests. */
  previewUnavailableLabel?: string
  /** Renders a "Download all" button above the list when provided and there's more than one item
   *  (a single-file ZIP isn't worth offering). */
  onDownloadAll?: () => void
  /** Label for the "Download all" button. Pass a translated string from the app. */
  downloadAllLabel?: ReactNode
  downloadAllDisabled?: boolean
  /** Classes for the outer wrapper. */
  className?: string
  /** Classes for each item card. Replaces the default entirely (not merged). */
  itemClassName?: string
  /** Inline style for each item card — see `iconStyle`. */
  itemStyle?: CSSProperties
  /** Classes for the row wrapping the info and actions groups. Replaces the default entirely. */
  headerClassName?: string
  /** Classes for the info group (icon + file name/subtitle). Replaces the default entirely. */
  infoClassName?: string
  /** Classes for the actions group (Download button + `renderExtraActions`). Replaces the default entirely. */
  actionsClassName?: string
  /** Classes for each item's kind icon. Replaces the default entirely (not merged). */
  iconClassName?: string
  /** Inline style for each item's kind icon — useful for per-event brand colors that can't be expressed as static Tailwind classes. */
  iconStyle?: CSSProperties
  /** Classes for each item's file name. Replaces the default entirely (not merged). */
  fileNameClassName?: string
  /** Classes for each item's Download button. Replaces the default entirely (not merged). */
  downloadButtonClassName?: string
  /** Inline style for each item's Download button — see `iconStyle`. */
  downloadButtonStyle?: CSSProperties
  /** Classes for the "Download all" button. Replaces the default entirely (not merged). */
  downloadAllButtonClassName?: string
  /** Inline style for the "Download all" button — see `iconStyle`. */
  downloadAllButtonStyle?: CSSProperties
  /** Renders extra per-item action buttons (e.g. delete) after the Download button. */
  renderExtraActions?: (item: MediaGalleryItem) => ReactNode
}

/**
 * Shared event-media file list: renders an inline preview appropriate to each file's kind (image,
 * video player, audio player, or PDF viewer), always alongside a Download button, plus an
 * optional "Download all" action for fetching every file as a single ZIP.
 *
 * Used by both the organiser's Event Media panel and the participant ticket page's "Event Media"
 * section, so the preview/download experience stays identical between the two. Contains no
 * app-specific data fetching — callers supply `getPreviewUrl` (typically a presigned-URL fetch)
 * and `onDownload`/`onDownloadAll` handlers.
 */
export function MediaGallery({
  items,
  onDownload,
  downloadLabel,
  emptyLabel,
  previewUnavailableLabel = 'Preview unavailable.',
  onDownloadAll,
  downloadAllLabel = 'Download all',
  downloadAllDisabled = false,
  className = '',
  itemClassName = DEFAULT_ITEM_CLASSES,
  itemStyle,
  headerClassName = DEFAULT_HEADER_CLASSES,
  infoClassName = DEFAULT_INFO_CLASSES,
  actionsClassName = DEFAULT_ACTIONS_CLASSES,
  iconClassName = DEFAULT_ICON_CLASSES,
  iconStyle,
  fileNameClassName = DEFAULT_FILENAME_CLASSES,
  downloadButtonClassName = DEFAULT_DOWNLOAD_BUTTON_CLASSES,
  downloadButtonStyle,
  downloadAllButtonClassName = DEFAULT_DOWNLOAD_ALL_BUTTON_CLASSES,
  downloadAllButtonStyle,
  renderExtraActions,
}: MediaGalleryProps) {
  return (
    <div className={`flex w-full flex-col gap-2.5 ${className}`}>
      {onDownloadAll && items.length > 1 && (
        <button
          type="button"
          onClick={onDownloadAll}
          disabled={downloadAllDisabled}
          className={`${BUTTON_INTERACTIVE_CLASSES} ${downloadAllButtonClassName}`}
          style={downloadAllButtonStyle}
        >
          {downloadAllLabel}
        </button>
      )}

      {items.length === 0
        ? emptyLabel
        : items.map((item) => (
            <MediaGalleryCard
              key={item.id}
              item={item}
              onDownload={onDownload}
              downloadLabel={downloadLabel}
              previewUnavailableLabel={previewUnavailableLabel}
              itemClassName={itemClassName}
              itemStyle={itemStyle}
              headerClassName={headerClassName}
              infoClassName={infoClassName}
              actionsClassName={actionsClassName}
              iconClassName={iconClassName}
              iconStyle={iconStyle}
              fileNameClassName={fileNameClassName}
              downloadButtonClassName={downloadButtonClassName}
              downloadButtonStyle={downloadButtonStyle}
              renderExtraActions={renderExtraActions}
            />
          ))}
    </div>
  )
}
